import { Link } from '@design-system/Link';
import { Button } from '@design-system/Button';
import { FormulaCard } from '@design-system/Cards/FormulaCard';
import { SHOP_PATH } from '@/constants/paths';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { useManageSubscriptionLink } from '@/hooks/useManageSubscriptionLink';
import type { FoodProduct } from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';
import { getMyIngredientsPageUrl } from '@/utils/nutrition/getMyIngredientsPageUrl';
import { NutritionPageCommonSkeleton } from './NutritionPageCommonSkeleton';
import { useBootup } from '@/hooks/useBootup';
import { cn } from '@/utils/styles';
import { EventTracker } from '@/utils';
import { LatestResultsBanner } from '@/components/shared/LatestResultsBanner';
import { TriSynbioticTaskCard } from '@screens/HomePage/TriSynbiotic/TriSynbioticTaskCard';
import { useShowFormulaRecs } from '@/hooks/useShowFormulaRecs';

export interface NutritionPageFormulaSectionProps {
  isLoading?: boolean;
  formulas?: FoodProduct[];
  isError?: boolean;
  'data-testid'?: string;
}

const trackShopRedirection = () => {
  EventTracker.trackEvent(
    'nutrition',
    'nutrition_Formulas_gateway_order_formulas',
  );
};

const trackSMPRedirection = () => {
  EventTracker.trackEvent(
    'nutrition',
    'nutrition_Formulas_gateway_manage_my_subscription',
  );
};

const trackIngredientRedirection = (name: string) => {
  EventTracker.trackEvent(
    'nutrition',
    `nutrition_Formulas_gateway_${name.replace(/\s+/g, '')}`,
  );
};

export const NutritionPageFormulaSection = ({
  formulas,
  isLoading,
  isError,
  'data-testid': testId,
}: NutritionPageFormulaSectionProps) => {
  const isFormulasSubscribed = formulas?.some(
    (item) => item.is_subscribed && !item.is_addon,
  );
  const hasAddOn = formulas?.some((item) => item.is_addon);
  const { bootupState } = useBootup();
  const manageSubscriptionLink = useManageSubscriptionLink();
  const showFormulaRecs = useShowFormulaRecs();

  if (isLoading) {
    return (
      <NutritionPageCommonSkeleton className="h-52 max-w-xl w-[10.5rem]" />
    );
  }

  if (isError || !formulas || formulas.length === 0) {
    return null;
  }

  return (
    <>
      <LatestResultsBanner className="mb-6" />
      <p className="mb-2 typography-h1 text-primary flex flex-row items-center gap-2" data-testid={testId}>
        {bootupState?.isEligibleForSupplements
          ? 'My Formulas'
          : 'My Supplements & Biotics'}
      </p>

      <p className="mb-6 typography-body text-tertiary lg:w-[35rem]" data-testid="nutrition-formulas-description">
        Accelerate your path to better health with supplements and biotics that
        fill nutrient gaps and target scores that need improvement.
      </p>

      <div
        className={cn(
          'w-full grid grid-cols-2 gap-2',
          'lg:grid lg:grid-cols-4 lg:gap-2',
          'lg:pb-4',
        )}
        data-testid="nutrition-formulas-grid"
      >
        {formulas?.filter((product) => !product.is_addon).map((product) => (
          <Link
            key={product.id}
            variant="flat"
            to={getMyIngredientsPageUrl({ product: product.name })}
            onClick={() => trackIngredientRedirection(product.name)}
            className="w-full"
            data-testid={`nutrition-formulas-card-${product.name.replace(/\s+/g, '-').toLowerCase()}`}
          >
            <FormulaCard
              title={product.name}
              formulaSrc={product.image_url}
              isSubscribed={product.is_subscribed}
              ingredientsCount={product.ingredients_count}
              isCustom={!bootupState?.isEligibleForSupplements}
              showFormulaRecs={showFormulaRecs}
              isAddOn={product.is_addon}
              className="w-full h-full"
              data-testid={`nutrition-formulas-card-${product.name.replace(/\s+/g, '-').toLowerCase()}`}
            />
          </Link>
        ))}
      </div>

      {bootupState?.isEligibleForSupplements && showFormulaRecs &&
        (isFormulasSubscribed ? (
          <Button
            asChild
            variant="secondary"
            className="w-full h-12 [&_svg]:!size-6 justify-between pr-3 mt-6"
            data-testid="nutrition-formulas-manageSubscriptionButton"
          >
            <Link to={manageSubscriptionLink} onClick={trackSMPRedirection}>
              Manage my subscription
              <ChevronRightIcon height={24} width={24} />
            </Link>
          </Button>
        ) : (
          <Button
            asChild
            variant="outline"
            className="dark bg-overlay-black w-full h-12 [&_svg]:!size-6 justify-between pr-3 mt-6"
            data-testid="nutrition-formulas-orderButton"
          >
            <Link to={SHOP_PATH} onClick={trackShopRedirection}>
              Order my formulas
              <ChevronRightIcon height={24} width={24} />
            </Link>
          </Button>
        ))}

      {hasAddOn && (
        <TriSynbioticTaskCard variant="formulas" />
      )}
    </>
  );
};
